import React, { useEffect, useState } from 'react';
import axios from 'axios';
import SupplierRebateSkuRateForm from './SupplierRebateSkuRateForm';
import RowActionsMenu from '../../../shared/RowActionsMenu';
import SavedFilterTabs from '../../../shared/SavedFilterTabs';
import { ActiveStatusPill } from '../../../shared/Pill';
import { PAGE_SIZE_OPTIONS, FALLBACK_PAGE_SIZE, useDefaultPageSize, getCachedDefaultPageSize, Pagination } from '../../../shared/pagination';
import { useErrorToast, useDismissError } from '../../../shared/ErrorToastContext';
import '../../../shared/ListRowHover.css';
import '../../../shared/StyledInput.css';
import { formatMoney as formatMoneyShared } from '../../../shared/numberFormat';
import { useScreenAccess } from '../../../shared/PermissionsContext';
import { askConfirm } from '../../../shared/AppDialogs';

// Same SavedFilterTabs pattern as Price Adjustments' own list — Default tab
// always resets to Active with no search/UoM/Rule narrowing; a saved tab can
// pin any combination, same as Price Adjustments' Status/UoM/dates.
const FILTER_SCREEN_KEY = 'supplier-rebate-sku-rates';
const DEFAULT_FILTERS = { status: 'all', search: '', uomId: '', ruleId: '' };
const FIELD_LABEL_STYLE = { fontSize: 12, color: '#5a6b85' };
const FIELD_INPUT_STYLE = { padding: '6px 10px', borderRadius: 6, border: '1px solid #bfc8d6', fontSize: 14, width: '100%' };

// '—' for blank; `currencyCode` marks the Supplier's currency (none = the
// home currency, e.g. the Dealer prices).
function formatMoney(value, currencyCode) {
  return formatMoneyShared(value, { currency: currencyCode || undefined }) || '—';
}

// Fixed Amount and Calculated are stored (and authoritative) per Base UoM
// (see vis_supplier_rebates.md's Fixed Amount section) — that's what stays
// consistent across a SKU's different sellable UoMs. Spelling out which UoM
// a figure is per matters here since two rows can have different Base UoMs
// — shown as a small grey caption under the dollar figure rather than
// inline, so the number itself stays the visually dominant thing in the cell.
function PerUomValue({ value, currencyCode, uomName, color }) {
  const money = formatMoney(value, currencyCode);
  if (money === '—') return <span style={{ color }}>—</span>;
  return (
    <span style={{ color }}>
      {money}
      {uomName && <><br /><span style={{ fontSize: 11, color: '#999' }}>per {uomName}</span></>}
    </span>
  );
}

// The per-Base-UoM figure scaled up by this row's conversion rate — what's
// actually earned each time the Tagged UoM itself sells (e.g. per Box), the
// number a Rebate Accrual will use once that story exists. Derived, not
// stored — never diverges from the per-Base-UoM figure it's scaled from.
function perTaggedUom(value, conversionRate) {
  if (value === null || value === undefined || Number.isNaN(Number(value))) return null;
  if (!conversionRate || Number.isNaN(Number(conversionRate))) return null;
  return Number(value) * Number(conversionRate);
}

function SupplierRebateSkuRateTable() {
  const { canCreate, canUpdate } = useScreenAccess();
  const showError = useErrorToast();
  const dismissError = useDismissError();
  const [showForm, setShowForm] = useState(false);
  const [editItem, setEditItem] = useState(null);
  const [formLoading, setFormLoading] = useState(false);
  const [rows, setRows] = useState([]);
  const [filters, setFilters] = useState(DEFAULT_FILTERS);
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(() => getCachedDefaultPageSize() || FALLBACK_PAGE_SIZE);
  const [pagination, setPagination] = useState({ page: 1, limit, total: 0, pages: 1 });
  const [loading, setLoading] = useState(false);
  // Tagged UoM / Rule dropdown options for the tab drawer — fetched once,
  // unpaginated, same split as Price Adjustments' own uoms/categories.
  const [uoms, setUoms] = useState([]);
  const [rules, setRules] = useState([]);
  // Collapsed by default — same "clutter otherwise" call as Price
  // Adjustments' own Quick Filters row; the badge on its toggle surfaces
  // whether Tagged UoM/Rule are narrowing the list while it's closed.
  const [quickRowOpen, setQuickRowOpen] = useState(false);

  useDefaultPageSize(setLimit);

  const updateFilter = (key, value) => { setFilters((prev) => ({ ...prev, [key]: value })); setPage(1); };
  const applyFilters = (values) => { setFilters(values); setPage(1); };

  // Badge on the Quick Filters toggle.
  const quickRowActiveCount = ['uomId', 'ruleId'].filter((key) => filters[key] !== DEFAULT_FILTERS[key]).length;

  useEffect(() => {
    axios.get('/api/uoms', { params: { status: 'active' } }).then((res) => setUoms(res.data.data || [])).catch(() => setUoms([]));
    axios.get('/api/supplier-rebate-rules', { params: { status: 'active' } }).then((res) => setRules(res.data.data || [])).catch(() => setRules([]));
  }, []);

  useEffect(() => {
    fetchRows();
    // eslint-disable-next-line
  }, [filters, page, limit]);

  const fetchRows = async () => {
    setLoading(true);
    try {
      const res = await axios.get('/api/supplier-rebate-sku-rates', {
        params: {
          status: filters.status,
          search: filters.search || undefined,
          uom_id: filters.uomId || undefined,
          rule_id: filters.ruleId || undefined,
          page,
          limit,
        },
      });
      setRows(res.data.data || []);
      setPagination(res.data.pagination || { page, limit, total: 0, pages: 1 });
    } catch (err) {
      setRows([]);
      setPagination({ page, limit, total: 0, pages: 1 });
    }
    setLoading(false);
  };

  const handleAdd = () => {
    setEditItem(null);
    setShowForm(true);
  };

  const handleEdit = (item) => {
    setEditItem(item);
    setShowForm(true);
  };

  const handleDeactivate = async (item) => {
    if (!(await askConfirm('Remove this SKU/UoM Rebate tag?', { confirmLabel: 'Remove', destructive: true }))) return;
    try {
      await axios.patch(`/api/supplier-rebate-sku-rates/${item.id}/deactivate`);
      fetchRows();
      dismissError();
    } catch (err) {
      showError(err, 'Deactivate failed');
    }
  };

  const handleSave = async (data) => {
    setFormLoading(true);
    try {
      if (editItem) {
        await axios.put(`/api/supplier-rebate-sku-rates/${editItem.id}`, data);
      } else {
        await axios.post('/api/supplier-rebate-sku-rates', data);
      }
      setShowForm(false);
      fetchRows();
      dismissError();
    } catch (err) {
      showError(err, 'Save failed');
    }
    setFormLoading(false);
  };

  return (
    <div style={{ margin: '32px 24px 48px', background: '#fafafa', border: '1px solid #eceef1', borderRadius: 12, boxShadow: '0 1px 6px rgba(0,0,0,0.04)', paddingBottom: 32 }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '20px 24px 0' }}>
        <h2 style={{ margin: 0, fontWeight: 600, fontSize: 20 }}>Supplier Rebate SKU Rates</h2>
        {canCreate && (
          <button
            onClick={handleAdd}
            disabled={showForm}
            title={showForm ? 'Finish editing first' : undefined}
            style={{ padding: '8px 18px', background: '#1976d2', color: '#fff', border: 'none', borderRadius: 6, fontWeight: 500, cursor: showForm ? 'not-allowed' : 'pointer', opacity: showForm ? 0.6 : 1 }}
          >
            Add SKU Rate
          </button>
        )}
      </div>
      <div style={{ padding: '4px 24px 0', color: '#666', fontSize: 13 }}>
        Tag a SKU's Unit of Measure against a Rebate Rule, and enter the Supplier's actual quoted Fixed Amount over Optim's calculated figure.
      </div>
      <div style={{ display: 'flex', gap: 10, margin: '16px 24px 0', alignItems: 'center', flexWrap: 'wrap' }}>
        <div>
          <input
            placeholder="Search SKU, name, UoM, or Rule..."
            value={filters.search}
            onChange={(e) => updateFilter('search', e.target.value)}
            style={{ padding: '8px 10px', borderRadius: 6, border: '1px solid #bfc8d6', fontSize: 14, width: 240 }}
          />
          {filters.search && (
            <button type="button" onClick={() => updateFilter('search', '')} title="Clear search" style={{ marginLeft: 6, padding: '4px 8px', fontSize: 12 }}>×</button>
          )}
        </div>
        <button
          type="button"
          onClick={() => setQuickRowOpen((o) => !o)}
          aria-expanded={quickRowOpen}
          style={{ padding: '8px 14px', background: '#fff', color: '#1976d2', border: '1px solid #1976d2', borderRadius: 6, fontWeight: 500, cursor: 'pointer' }}
        >
          {quickRowOpen ? 'Quick Filters ▲' : 'Quick Filters ▼'}
          {quickRowActiveCount > 0 && (
            <span style={{ display: 'inline-flex', alignItems: 'center', justifyContent: 'center', minWidth: 18, height: 18, padding: '0 5px', marginLeft: 6, borderRadius: 999, background: '#1976d2', color: '#fff', fontSize: 11, fontWeight: 700 }}>
              {quickRowActiveCount}
            </span>
          )}
        </button>
      </div>

      {/* Quick row — ad hoc, un-saved narrowing on top of whichever tab is
          selected (Tagged UoM, Rule). Also present in the drawer so a
          combination worth keeping can still be saved as a tab. Collapsed
          by default, same as Price Adjustments' own Quick Filters. */}
      {quickRowOpen && (
        <div style={{ display: 'flex', gap: 14, margin: '10px 24px 0', alignItems: 'flex-end', flexWrap: 'wrap' }}>
          <div>
            <label style={{ fontSize: 12, color: '#5a6b85' }}>Tagged UoM</label><br />
            <select className="styled-input" value={filters.uomId} onChange={(e) => updateFilter('uomId', e.target.value)} style={{ padding: '6px 10px', borderRadius: 6, border: '1px solid #bfc8d6', fontSize: 14, width: 160 }}>
              <option value="">All UoMs</option>
              {uoms.map((u) => (
                <option key={u.id} value={u.id}>{u.name}</option>
              ))}
            </select>
          </div>
          <div>
            <label style={{ fontSize: 12, color: '#5a6b85' }}>Rule</label><br />
            <select className="styled-input" value={filters.ruleId} onChange={(e) => updateFilter('ruleId', e.target.value)} style={{ padding: '6px 10px', borderRadius: 6, border: '1px solid #bfc8d6', fontSize: 14, width: 200 }}>
              <option value="">All Rules</option>
              {rules.map((r) => (
                <option key={r.id} value={r.id}>{r.name}</option>
              ))}
            </select>
          </div>
        </div>
      )}

      <div style={{ margin: '10px 24px 0' }}>
        <SavedFilterTabs
          screenKey={FILTER_SCREEN_KEY}
          defaultFilters={DEFAULT_FILTERS}
          currentFilters={filters}
          onApply={applyFilters}
          renderFields={(draft, setDraftField) => (
            <>
              <div>
                <label style={FIELD_LABEL_STYLE}>Search</label><br />
                <input
                  placeholder="Search SKU, name, UoM, or Rule..."
                  value={draft.search}
                  onChange={(e) => setDraftField('search', e.target.value)}
                  style={FIELD_INPUT_STYLE}
                />
              </div>
              <div>
                <label style={FIELD_LABEL_STYLE}>Tagged UoM</label><br />
                <select className="styled-input" value={draft.uomId} onChange={(e) => setDraftField('uomId', e.target.value)} style={FIELD_INPUT_STYLE}>
                  <option value="">All UoMs</option>
                  {uoms.map((u) => (
                    <option key={u.id} value={u.id}>{u.name}</option>
                  ))}
                </select>
              </div>
              <div>
                <label style={FIELD_LABEL_STYLE}>Rule</label><br />
                <select className="styled-input" value={draft.ruleId} onChange={(e) => setDraftField('ruleId', e.target.value)} style={FIELD_INPUT_STYLE}>
                  <option value="">All Rules</option>
                  {rules.map((r) => (
                    <option key={r.id} value={r.id}>{r.name}</option>
                  ))}
                </select>
              </div>
              <div>
                <label style={FIELD_LABEL_STYLE}>Status</label><br />
                <select className="styled-input" value={draft.status} onChange={(e) => setDraftField('status', e.target.value)} style={FIELD_INPUT_STYLE}>
                  <option value="all">All items</option>
                  <option value="active">Active items</option>
                  <option value="inactive">Deactivated items</option>
                </select>
              </div>
            </>
          )}
        />
      </div>

      {showForm && (
        <div style={{ background: '#f7f9fc', border: '1px solid #e3e7ee', padding: 20, margin: '18px 24px 0', borderRadius: 8 }}>
          <SupplierRebateSkuRateForm
            key={editItem?.id ?? 'add'}
            initial={editItem}
            onSave={handleSave}
            onCancel={() => { setShowForm(false); dismissError(); }}
            loading={formLoading}
          />
        </div>
      )}

      <div style={{ margin: '24px 24px 0', background: '#fff', border: '1px solid #b3bbc7', borderRadius: 10, overflow: 'visible', boxShadow: '0 1px 4px rgba(0,0,0,0.04)' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 16, tableLayout: 'fixed' }}>
          <colgroup>
            <col style={{ width: '18%' }} />
            <col style={{ width: '8%' }} />
            <col style={{ width: '11%' }} />
            <col style={{ width: '12%' }} />
            <col style={{ width: '12%' }} />
            <col style={{ width: '12%' }} />
            <col style={{ width: '12%' }} />
            <col style={{ width: '7%' }} />
            <col style={{ width: '4%' }} />
            <col style={{ width: '4%' }} />
          </colgroup>
          <thead className="list-head" style={{ fontSize: 14 }}>
            <tr>
              <th style={{ padding: '12px 8px', fontWeight: 600, borderBottom: '1px solid #b3bbc7', textAlign: 'left' }}>
                Product<br /><span style={{ fontWeight: 400, fontSize: 12, color: '#888' }}>SKU</span>
              </th>
              <th style={{ padding: '12px 8px', fontWeight: 600, borderBottom: '1px solid #b3bbc7', textAlign: 'left' }}>Tagged UoM</th>
              <th style={{ padding: '12px 8px', fontWeight: 600, borderBottom: '1px solid #b3bbc7', textAlign: 'left' }}>Rule</th>
              <th style={{ padding: '12px 8px', fontWeight: 600, borderBottom: '1px solid #b3bbc7', textAlign: 'right' }} title="The stored, authoritative figure — stays consistent across this SKU's different sellable UoMs">
                Fixed<br /><span style={{ fontWeight: 400, fontSize: 12, color: '#888' }}>(per Base UoM)</span>
              </th>
              <th style={{ padding: '12px 8px', fontWeight: 600, borderBottom: '1px solid #b3bbc7', textAlign: 'right' }} title="The stored, authoritative figure — stays consistent across this SKU's different sellable UoMs">
                Calc.<br /><span style={{ fontWeight: 400, fontSize: 12, color: '#888' }}>(per Base UoM)</span>
              </th>
              <th style={{ padding: '12px 8px', fontWeight: 600, borderBottom: '1px solid #b3bbc7', textAlign: 'right' }} title="What's actually earned each time the Tagged UoM sells — the per-Base-UoM figure scaled by this SKU's conversion rate">
                Fixed<br /><span style={{ fontWeight: 400, fontSize: 12, color: '#888' }}>(per Tagged UoM)</span>
              </th>
              <th style={{ padding: '12px 8px', fontWeight: 600, borderBottom: '1px solid #b3bbc7', textAlign: 'right' }} title="What's actually earned each time the Tagged UoM sells — the per-Base-UoM figure scaled by this SKU's conversion rate">
                Calc.<br /><span style={{ fontWeight: 400, fontSize: 12, color: '#888' }}>(per Tagged UoM)</span>
              </th>
              <th style={{ padding: '12px 8px', fontWeight: 600, borderBottom: '1px solid #b3bbc7', textAlign: 'center' }}>Status</th>
              <th style={{ padding: '12px 8px', fontWeight: 600, borderBottom: '1px solid #b3bbc7', textAlign: 'center' }}></th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr><td colSpan={9} style={{ padding: '14px', textAlign: 'center', background: '#f6f8fa', fontSize: 14 }}>Loading...</td></tr>
            ) : rows.length === 0 ? (
              <tr><td colSpan={9} style={{ padding: '14px', textAlign: 'center', background: '#f6f8fa', fontSize: 14 }}>No results</td></tr>
            ) : rows.map((item) => {
              const currencyCode = item.rule?.supplier?.currency_code;
              const baseUomName = item.product_uom?.product?.base_uom?.name;
              const taggedUomName = item.product_uom?.uom?.name;
              const conversionRate = item.product_uom?.conversion_rate;
              const diverges = item.calculated?.calculatedAmount !== null && item.calculated?.calculatedAmount !== undefined
                && Math.abs(Number(item.fixed_amount) - item.calculated.calculatedAmount) > 0.01;
              return (
                <tr
                  key={item.id}
                  className="list-row"
                  onClick={() => { if (!showForm && canUpdate) handleEdit(item); }}
                  style={{ cursor: showForm || !canUpdate ? 'default' : 'pointer' }}
                  title={showForm || !canUpdate ? undefined : 'Click to edit'}
                >
                  <td style={{ padding: '6px 8px', fontSize: 14 }} title={item.product_uom?.product?.name}>
                    {item.product_uom?.product?.sku || '—'}
                  </td>
                  <td style={{ padding: '6px 8px', fontSize: 14 }}>{taggedUomName || '—'}</td>
                  <td style={{ padding: '6px 8px', fontSize: 14, fontWeight: 600 }}>{item.rule?.name || '—'}</td>
                  <td
                    style={{ padding: '6px 8px', fontSize: 14, textAlign: 'right', lineHeight: 1.3 }}
                    title={diverges ? "Overrides Optim's calculated figure by more than a cent — double check it's not a unit mismatch" : ''}
                  >
                    <PerUomValue value={item.fixed_amount} currencyCode={currencyCode} uomName={baseUomName} color={diverges ? '#a15c00' : undefined} />
                  </td>
                  <td style={{ padding: '6px 8px', fontSize: 14, textAlign: 'right', lineHeight: 1.3 }}>
                    {item.calculated
                      ? <PerUomValue value={item.calculated.calculatedAmount} currencyCode={currencyCode} uomName={baseUomName} color="#666" />
                      : '—'}
                  </td>
                  <td
                    style={{ padding: '6px 8px', fontSize: 14, textAlign: 'right', lineHeight: 1.3 }}
                    title={diverges ? "Overrides Optim's calculated figure by more than a cent — double check it's not a unit mismatch" : ''}
                  >
                    <PerUomValue value={perTaggedUom(item.fixed_amount, conversionRate)} currencyCode={currencyCode} uomName={taggedUomName} color={diverges ? '#a15c00' : undefined} />
                  </td>
                  <td style={{ padding: '6px 8px', fontSize: 14, textAlign: 'right', lineHeight: 1.3 }}>
                    {item.calculated
                      ? <PerUomValue value={perTaggedUom(item.calculated.calculatedAmount, conversionRate)} currencyCode={currencyCode} uomName={taggedUomName} color="#666" />
                      : '—'}
                  </td>
                  <td style={{ padding: '6px 8px', textAlign: 'center', fontSize: 13 }}>
                    <ActiveStatusPill active={item.is_active} />
                  </td>
                  <td style={{ padding: '6px 8px', textAlign: 'center' }} onClick={(e) => e.stopPropagation()}>
                    <RowActionsMenu
                      items={[
                        { key: 'edit', label: 'Edit', disabled: showForm, disabledTitle: showForm ? 'Finish editing first' : undefined, onClick: () => handleEdit(item) },
                        item.is_active
                          ? { key: 'deactivate', label: 'Remove', danger: true, disabled: showForm, disabledTitle: showForm ? 'Finish editing first' : undefined, onClick: () => handleDeactivate(item) }
                          : null,
                      ].filter(Boolean)}
                    />
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      <Pagination
        pagination={pagination}
        setPage={setPage}
        rightContent={(
          <select className="styled-input" value={limit} onChange={(e) => { setLimit(Number(e.target.value)); setPage(1); }} style={{ padding: '6px 10px', borderRadius: 6, border: '1px solid #bfc8d6', fontSize: 14 }}>
            {PAGE_SIZE_OPTIONS.map((sz) => <option key={sz} value={sz}>{sz} per page</option>)}
          </select>
        )}
      />
    </div>
  );
}

export default SupplierRebateSkuRateTable;
